// 2026-09-09：比较实际入口包而非后端版本；普通浏览时自动更新，保留支付、输入和填写中的页面。
export function webEntryFingerprint(html:string,origin:string):string{
  const assets=Array.from(html.matchAll(/<(?:script|link)\b[^>]*\b(?:src|href)\s*=\s*["']([^"']+)["'][^>]*>/gi))
  const paths=assets.map(match=>new URL(match[1],origin)).filter(url=>url.origin===new URL(origin).origin
    && /\/(?:app|chunk-vendors)\.[a-z0-9_-]+\.(?:js|css)$/i.test(url.pathname)).map(url=>url.pathname)
  return Array.from(new Set(paths)).sort().join('|')
}
type UpdateOptions={initial:string;now:()=>number;visible:()=>boolean;safe:()=>boolean;
  load:()=>Promise<string>;reload:()=>void;schedule:(callback:()=>void)=>unknown;cancel:(timer:unknown)=>void;
  readAttempt:()=>string;saveAttempt:(target:string)=>void}
export function createWebEntryUpdateCoordinator(options:UpdateOptions){
  let busy=false,disposed=false,lastCheck=-Infinity,pending=''
  let timer:unknown
  const attempted=new Set<string>()
  const cancel=()=>{if(timer!==undefined){options.cancel(timer);timer=undefined}}
  const canApply=()=>!disposed && !busy && !!pending && options.visible() && options.safe()
    && !attempted.has(pending) && options.readAttempt()!==pending
  // 2026-09-09：点击和路由切换完成后再次校验，免确认自动刷新；每个目标只尝试一次，避免旧缓存刷新循环。
  const scheduleUpdate=()=>{
    cancel()
    if(!canApply()) return
    const target=pending
    timer=options.schedule(()=>{
      timer=undefined
      if(pending!==target || !canApply()) return
      attempted.add(target)
      options.saveAttempt(target)
      options.reload()
    })
  }
  return {
    async check(){
      if(disposed || busy || !options.initial || !options.visible() || options.now()-lastCheck<60000){scheduleUpdate();return}
      busy=true;lastCheck=options.now()
      // 2026-09-09：重新联网确认期间暂停旧待更新任务；检查失败保留当前页面，不用过期结果触发刷新。
      cancel()
      try{
        const latest=await options.load()
        if(disposed) return
        pending=latest && latest!==options.initial?latest:''
      }catch(_error){pending='';/* 检查失败只保留当前页面，绝不改变登录态、机构或草稿。 */}
      finally{busy=false;scheduleUpdate()}
    },
    dispose(){disposed=true;cancel()}
  }
}
export const isSafeWebUpdateRoute=(path:string)=>new Set([
  '/','/main','/main/','/main/index','/main/bokes','/main/retails','/main/cloths','/main/fabrics',
  '/main/accessories','/main/decorations','/main/helpcenter'
]).has(path.toLowerCase())

export function installWebEntryUpdate(router:any){
  const storageKey='ldy-web-entry-refresh-attempt'
  let edited=false
  let navigationReady=false,navigating=false
  let currentPath=router.currentRoute.value.fullPath || router.currentRoute.value.path
  const editable='input,textarea,select,[contenteditable="true"],[role="textbox"]'
  const coordinator=createWebEntryUpdateCoordinator({
    initial:webEntryFingerprint(document.documentElement.outerHTML,window.location.origin),now:()=>Date.now(),
    visible:()=>document.visibilityState==='visible',
    safe:()=>navigationReady && !navigating && isSafeWebUpdateRoute(router.currentRoute.value.path) && !edited
      && !document.activeElement?.matches(editable)
      && !document.querySelector('.ant-drawer-open')
      && !Array.from(document.querySelectorAll('.ant-modal-wrap')).some(element=>element.getClientRects().length>0),
    load:async()=>{
      const controller=new AbortController(),timeout=window.setTimeout(()=>controller.abort(),8000)
      try{
        const response=await fetch(new URL('/index.html',window.location.origin).href,
          {cache:'no-store',credentials:'same-origin',redirect:'error',signal:controller.signal})
        if(!response.ok || !response.headers.get('content-type')?.includes('text/html')) return ''
        const html=await response.text()
        return html.length<=1048576?webEntryFingerprint(html,window.location.origin):''
      }finally{window.clearTimeout(timeout)}
    },reload:()=>window.location.reload(),
    schedule:callback=>window.setTimeout(callback,800),cancel:timer=>window.clearTimeout(timer as number),
    readAttempt:()=>{try{return sessionStorage.getItem(storageKey) || ''}catch(_error){return ''}},
    saveAttempt:target=>{try{sessionStorage.setItem(storageKey,target)}catch(_error){/* 禁用存储时仍由本页内存防止重复更新。 */}}
  })
  const check=()=>{void coordinator.check()}
  // 2026-09-09：保护当前页已输入的搜索或表单内容；用户离开该页面后再允许自动更新。
  const edit=(event:Event)=>{if((event.target as Element | null)?.closest?.(editable)) edited=true;check()}
  const routeChanged=()=>{
    navigating=false
    const next=router.currentRoute.value.fullPath || router.currentRoute.value.path
    if(next!==currentPath){edited=false;currentPath=next}
    check()
  }
  window.addEventListener('pageshow',check)
  document.addEventListener('visibilitychange',check)
  document.addEventListener('click',check)
  document.addEventListener('focusout',check)
  document.addEventListener('input',edit)
  document.addEventListener('change',edit)
  // 2026-09-09：等待首个路由及懒加载/导航守卫完成，不能按跳转前的浏览页判断可以刷新。
  const removeBeforeHook=router.beforeEach(()=>{navigating=true;check();return true})
  const removeErrorHook=router.onError(()=>{navigating=false;check()})
  const removeRouteHook=router.afterEach(routeChanged)
  void router.isReady().then(()=>{navigationReady=true;check()}).catch(()=>{/* 路由未初始化成功时保留现场，不自动刷新。 */})
  check()
  return ()=>{
    coordinator.dispose();removeBeforeHook();removeErrorHook();removeRouteHook();window.removeEventListener('pageshow',check)
    document.removeEventListener('visibilitychange',check);document.removeEventListener('click',check)
    document.removeEventListener('focusout',check);document.removeEventListener('input',edit);document.removeEventListener('change',edit)
  }
}
